import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Award, Target, Users, TrendingUp, Shield, Factory } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AboutUs = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  const values = [
    {
      icon: Shield,
      title: t('about.values.quality.title'),
      description: t('about.values.quality.description'),
    },
    {
      icon: Users,
      title: t('about.values.customer.title'),
      description: t('about.values.customer.description'),
    },
    {
      icon: TrendingUp,
      title: t('about.values.innovation.title'),
      description: t('about.values.innovation.description'),
    },
    {
      icon: Award,
      title: t('about.values.excellence.title'),
      description: t('about.values.excellence.description'),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-[#4A5F8F] to-[#2C3E5F] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">{t('about.title')}</h1>
            <p className="text-xl text-gray-100 max-w-3xl mx-auto">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('about.story.title')}</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('about.story.para1') }} />
              <p className="text-lg text-gray-700 mb-6 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('about.story.para2') }} />
              <p className="text-lg text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('about.story.para3') }} />
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/gviwsb6a_Gemini_Generated_Image_156kmz156kmz156k.png" 
                alt="Dharani Pipes Manufacturing Facility" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('about.leadership.title')}</h2>
          </div>
          
          <Card className="max-w-5xl mx-auto shadow-xl">
            <CardContent className="p-8 md:p-12">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-[#4A5F8F] mb-2">{currentLang === 'en' ? 'Mr. Basavaraj Dambal' : t('about.leadership.name')}</h3>
                <p className="text-xl text-gray-600 font-medium">{t('about.leadership.position')}</p>
              </div>
              
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p dangerouslySetInnerHTML={{ __html: t('about.leadership.para1') }} />
                <p dangerouslySetInnerHTML={{ __html: t('about.leadership.para2') }} />
                <p dangerouslySetInnerHTML={{ __html: t('about.leadership.para3') }} />
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Managing Director's Message */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('about.message.title')}</h2>
          </div>
          
          <Card className="max-w-5xl mx-auto shadow-xl border-l-4 border-[#4A5F8F]">
            <CardContent className="p-8 md:p-12">
              <div className="relative">
                <div className="text-6xl text-[#4A5F8F] opacity-20 absolute -top-4 -left-2">"</div>
                <div className="relative z-10">
                  <p className="text-lg text-gray-700 leading-relaxed mb-6 italic">
                    {t('about.message.quote1')}
                  </p>
                  
                  <p className="text-lg text-gray-700 leading-relaxed mb-6 italic">
                    {t('about.message.quote2')}
                  </p>
                </div>
                <div className="text-6xl text-[#4A5F8F] opacity-20 absolute -bottom-8 right-0">"</div>
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-xl font-bold text-gray-900">{t('about.message.signature')}</p>
                <p className="text-gray-600">{t('about.message.position')}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Sustainability - Solar Power */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('about.sustainability.title')}</h2>
            <p className="text-xl text-gray-600">{t('about.sustainability.subtitle')}</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Card className="shadow-xl border-none bg-gradient-to-br from-green-50 to-blue-50">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-green-500 bg-opacity-20 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900">{t('about.sustainability.solarTitle')}</h3>
                    <p className="text-gray-600">{t('about.sustainability.solarSubtitle')}</p>
                  </div>
                </div>
                
                <p className="text-lg text-gray-700 leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: t('about.sustainability.description') }} />
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                    <p className="text-gray-700">{t('about.sustainability.benefit1')}</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                    <p className="text-gray-700">{t('about.sustainability.benefit2')}</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                    <p className="text-gray-700">{t('about.sustainability.benefit3')}</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                    <p className="text-gray-700">{t('about.sustainability.benefit4')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="relative h-[400px] rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/ianou2xm_rooftop-solar-regulation.jpeg" 
                alt="Dharani Pipes rooftop solar installation" 
                className="w-full h-full object-cover brightness-105 contrast-110"
                style={{ filter: 'saturate(1.1)' }}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
                <p className="text-white text-lg font-semibold">Rooftop Solar Installation at Dharani Pipes Facility, Koppal</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Core Values - Moved to Bottom */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('about.values.title')}</h2>
            <p className="text-xl text-gray-600">{t('about.values.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden flex flex-col h-full">
                <CardContent className="p-6 text-center flex-grow">
                  <div className="w-16 h-16 bg-[#4A5F8F] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-8 h-8 text-[#4A5F8F]" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
                {/* Blue accent line at bottom */}
                <div className="h-1 bg-[#4A5F8F] mt-auto" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
